import moment, { Moment } from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { IconStar } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { CustomerReview } from "@shared/graphql";
import { actions } from "../i18n/sharable-defs";
import { SkeletonableProps } from "../tools/components";
import { useElementSize } from "../hooks/dom";
import Button from "./common/button";
import "./review-card.scss"

type Props = SkeletonableProps<CustomerReview> & {
  fullDisplay?: boolean;
  onMore?: (date: CustomerReview) => void;
};

const ReviewCard = (
  {
    loading,
    className,
    data,
    fullDisplay = false,
    onMore = () => void 0
  }: Props) => {

  const intl = useIntl();
  const { ref, size } = useElementSize<HTMLDivElement>();
  const [ moreable, setMoreable ] = useState<boolean>(false);

  useEffect(() => {
    if (!ref.current) return;
    setMoreable(ref.current.scrollHeight > ref.current.clientHeight);
  }, [ data?.comment, size?.height ]);

  const formatDate = useCallback((value: Moment | string) =>  moment.isMoment(value)
    ? intl.formatDate(value.toDate(), { year: "numeric", month: "long" })
    : value, [ intl ]);
  
  const wrapperClasses = classNames("review-card", className, { full: fullDisplay });

  if (loading || !data)
    return (<Skeleton className={wrapperClasses} inline={true} />);

  return (
    <article className={wrapperClasses}>

      <div className="client">
        <div className="name">
          <span className="ellipsed">{data.customerName}</span>
          {!!data.averageRate &&
          <>
            <IconStar className="s-icon" />
            <span className="rate">
              {intl.formatNumber(data.averageRate, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
            </span>
          </>
          }
        </div>
        <div className="complement">
          {formatDate(data.reviewedAt)}
        </div>
      </div>

      <div ref={ref}
           className="comment html-format"
           dangerouslySetInnerHTML={{ __html: data.comment }} />

      {(moreable || !!data.answer) &&
        <Button className="small link"
                onClick={() => onMore(data)}>
          {intl.formatMessage(actions.more)}
        </Button>
      }

    </article>);

}

export default ReviewCard;
